import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';

// Hook pour gérer la liste des clients
export const useClients = (initialFilters = {}) => {
  const { apiCall, API_BASE_URL } = useApi();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Charger les clients
  const loadClients = useCallback(async (newFilters = {}) => {
    try {
      // Vérifier la disponibilité du token et des données utilisateur
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger les clients');
      setClients([]);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const combinedFilters = { ...filters, ...newFilters };
      const queryParams = new URLSearchParams();
      
      if (combinedFilters.status) queryParams.append('status', combinedFilters.status);
      if (combinedFilters.type) queryParams.append('type', combinedFilters.type);
      if (combinedFilters.search) queryParams.append('search', combinedFilters.search);
      if (combinedFilters.page) queryParams.append('page', combinedFilters.page);
      if (combinedFilters.limit) queryParams.append('limit', combinedFilters.limit);
      if (combinedFilters.sortBy) queryParams.append('sortBy', combinedFilters.sortBy);
      if (combinedFilters.sortOrder) queryParams.append('sortOrder', combinedFilters.sortOrder);

      const endpoint = `/clients${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const data = await apiCall(endpoint);
      
      setClients(data.data || []);
      setPagination(data.pagination || {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      });
    } catch (err) {
      console.error('❌ Erreur dans loadClients:', err);
      console.error('❌ Erreur API clients:', err.message);
      setError(err.message);
      setClients([]);
    } finally {
      setLoading(false);
    }
    } catch (outerErr) {
      console.error('❌ Erreur FATALE dans loadClients:', outerErr);
      setError(outerErr.message);
      setLoading(false);
    }
  }, [filters, apiCall]);

  // Mettre à jour les filtres
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Supprimer un client
  const deleteClient = useCallback(async (clientId) => {
    try {
      await apiCall(`/clients/${clientId}`, {
        method: 'DELETE'
      });
      setClients(prev => prev.filter(client => client.id !== clientId));
      return true;
    } catch (err) {
      console.error('Erreur suppression client:', err.message);
      setError(err.message);
      return false;
    }
  }, [apiCall]);

  // Changer le statut d'un client
  const toggleClientStatus = useCallback(async (clientId, newStatus) => {
    try {
      await apiCall(`/clients/${clientId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      setClients(prev => prev.map(client => 
        client.id === clientId ? { ...client, status: newStatus } : client
      ));
      return true;
    } catch (err) {
      console.error('Erreur changement statut client:', err.message);
      setError(err.message);
      return false;
    }
  }, [apiCall]);

  // Charger les clients au montage du composant et quand les filtres changent
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadClients();
    }, 300); // Debounce de 300ms
    
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return {
    clients,
    loading,
    error,
    filters,
    pagination,
    loadClients,
    updateFilters,
    deleteClient,
    toggleClientStatus,
    refetch: loadClients
  };
};

// Hook pour gérer un client spécifique
export const useClient = (clientId) => {
  const { apiCall } = useApi();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Charger les données du client
  const loadClient = useCallback(async () => {
    console.log('🔍 useClient - loadClient appelé avec clientId:', clientId);
    if (!clientId) {
      console.log('⚠️ useClient - Pas de clientId, arrêt');
      return;
    }
    
    // Vérifier la disponibilité du token et des données utilisateur
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger le client');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 useClient - Appel API pour client:', clientId);
      const data = await apiCall(`/clients/${clientId}`);
      console.log('✅ useClient - Réponse API:', data);
      setClient(data);
    } catch (err) {
      console.error('❌ useClient - Erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, apiCall]);

  // Mettre à jour le client
  const updateClient = useCallback(async (clientData) => {
    try {
      const data = await apiCall(`/clients/${clientId}`, {
        method: 'PUT',
        body: JSON.stringify(clientData)
      });
      setClient(data.data);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [clientId, apiCall]);

  // Réinitialiser le mot de passe
  const resetPassword = useCallback(async () => {
    try {
      await apiCall(`/clients/${clientId}/reset-password`, {
        method: 'POST'
      });
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [clientId, apiCall]);

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  return {
    client,
    loading,
    error,
    updateClient,
    resetPassword,
    refetch: loadClient
  };
};

// Hook pour gérer les employés d'un client
export const useClientEmployees = (clientId) => {
  const { apiCall } = useApi();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Charger les employés
  const loadEmployees = useCallback(async () => {
    if (!clientId) return;
    
    // Vérifier la disponibilité du token et des données utilisateur
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger les employés');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiCall(`/clients/${clientId}/employees`);
      setEmployees(data.data || []);
    } catch (err) {
      setError(err.message);
      console.error('Erreur lors du chargement des employés:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId, apiCall]);

  // Ajouter un employé
  const addEmployee = useCallback(async (employeeData) => {
    try {
      const data = await apiCall(`/clients/${clientId}/employees`, {
        method: 'POST',
        body: JSON.stringify(employeeData)
      });
      setEmployees(prev => [...prev, data.data]);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [clientId, apiCall]);

  // Mettre à jour un employé
  const updateEmployee = useCallback(async (employeeId, employeeData) => {
    try {
      const data = await apiCall(`/clients/${clientId}/employees/${employeeId}`, {
        method: 'PUT',
        body: JSON.stringify(employeeData)
      });
      setEmployees(prev => prev.map(emp => 
        emp.id === employeeId ? data.data : emp
      ));
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [clientId, apiCall]);

  // Supprimer un employé
  const deleteEmployee = useCallback(async (employeeId) => {
    try {
      await apiCall(`/clients/${clientId}/employees/${employeeId}`, {
        method: 'DELETE'
      });
      setEmployees(prev => prev.filter(emp => emp.id !== employeeId));
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [clientId, apiCall]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  return {
    employees,
    loading,
    error,
    addEmployee,
    updateEmployee,
    deleteEmployee,
    refetch: loadEmployees
  };
};

// Hook pour gérer les signalements d'un client
export const useClientSignalements = (clientId, initialFilters = {}) => {
  const { apiCall } = useApi();
  const [signalements, setSignalements] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    byGravity: { faible: 0, moyenne: 0, critique: 0 },
    byService: {}
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);

  const loadSignalements = useCallback(async (newFilters = {}) => {
    if (!clientId) return;

    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger les signalements');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const combinedFilters = { ...filters, ...newFilters };
      const queryParams = new URLSearchParams();
      if (combinedFilters.status) queryParams.append('status', combinedFilters.status);
      if (combinedFilters.service) queryParams.append('service', combinedFilters.service);
      if (combinedFilters.page) queryParams.append('page', combinedFilters.page);
      if (combinedFilters.limit) queryParams.append('limit', combinedFilters.limit);

      const endpoint = `/clients/${clientId}/reports${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const data = await apiCall(endpoint);

      const signalementsData = data.data || [];
      setSignalements(signalementsData);

      // Calculer les statistiques
      const totalCount = signalementsData.length;
      const gravityCount = { faible: 0, moyenne: 0, critique: 0 };
      const serviceCount = {};

      signalementsData.forEach(s => {
        const gravity = (s.severity || s.gravity || 'faible').toLowerCase();
        if (gravityCount[gravity] !== undefined) {
          gravityCount[gravity]++;
        }

        const service = s.service_type || s.service || 'Autre';
        serviceCount[service] = (serviceCount[service] || 0) + 1;
      });

      setStats({
        total: totalCount,
        byGravity: gravityCount,
        byService: serviceCount
      });
    } catch (err) {
      console.error('Erreur lors du chargement des signalements:', err);
      setError(err.message);
      setSignalements([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, filters, apiCall]);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  useEffect(() => {
    loadSignalements();
  }, [loadSignalements]);

  return {
    signalements,
    stats,
    loading,
    error,
    filters,
    updateFilters,
    refetch: loadSignalements
  };
};

// Hook pour gérer les demandes/signalements d'un client (legacy)
export const useClientReports = (clientId, initialFilters = {}) => {
  const { apiCall } = useApi();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);

  // Charger les demandes
  const loadReports = useCallback(async (newFilters = {}) => {
    if (!clientId) return;

    // Vérifier la disponibilité du token et des données utilisateur
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger les rapports');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const combinedFilters = { ...filters, ...newFilters };
      const queryParams = new URLSearchParams();
      if (combinedFilters.status) queryParams.append('status', combinedFilters.status);
      if (combinedFilters.type) queryParams.append('type', combinedFilters.type);
      if (combinedFilters.search) queryParams.append('search', combinedFilters.search);

      const endpoint = `/clients/${clientId}/reports${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const data = await apiCall(`${endpoint}`);
      setReports(data.data || []);
    } catch (err) {
      setError(err.message);
      console.error('Erreur lors du chargement des demandes:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId, filters, apiCall]);

  // Supprimer une demande
  const deleteReport = useCallback(async (reportId) => {
    try {
      await apiCall(`/reports/${reportId}`, {
        method: 'DELETE'
      });
      setReports(prev => prev.filter(report => report.id !== reportId));
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [apiCall]);

  // Mettre à jour une demande
  const updateReport = useCallback(async (reportId, reportData) => {
    try {
      const data = await apiCall(`/reports/${reportId}`, {
        method: 'PUT',
        body: JSON.stringify(reportData)
      });
      setReports(prev => prev.map(report => 
        report.id === reportId ? data.data : report
      ));
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [apiCall]);

  // Mettre à jour les filtres
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  return {
    reports,
    loading,
    error,
    filters,
    updateFilters,
    deleteReport,
    updateReport,
    refetch: loadReports
  };
};

// Hook pour la messagerie
export const useMessaging = () => {
  const { apiCall } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Envoyer un message
  const sendMessage = useCallback(async (messageData) => {
    setLoading(true);
    setError(null);
    
    try {
      await apiCall(`/clients/message`, {
        method: 'POST',
        body: JSON.stringify(messageData)
      });
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  // Envoyer une publicité
  const sendPublicity = useCallback(async (publicityData) => {
    setLoading(true);
    setError(null);
    
    try {
      await apiCall(`/clients/publicity`, {
        method: 'POST',
        body: JSON.stringify(publicityData)
      });
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  return {
    loading,
    error,
    sendMessage,
    sendPublicity
  };
};

// Hook pour gérer l'activité d'un client
export const useClientActivity = (clientId, initialFilters = {}) => {
  const { apiCall } = useApi();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });

  const loadActivities = useCallback(async (newFilters = {}) => {
    if (!clientId) return;
    
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger l\'activité');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const combinedFilters = { ...filters, ...newFilters };
      const queryParams = new URLSearchParams();
      if (combinedFilters.page) queryParams.append('page', combinedFilters.page);
      if (combinedFilters.limit) queryParams.append('limit', combinedFilters.limit);

      const endpoint = `/clients/${clientId}/activity${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const data = await apiCall(endpoint);
      
      setActivities(data.data || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0 });
    } catch (err) {
      console.error('Erreur lors du chargement de l\'activité:', err);
      setError(err.message);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, filters, apiCall]);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  return {
    activities,
    loading,
    error,
    filters,
    pagination,
    updateFilters,
    refetch: loadActivities
  };
};

// Hook pour gérer les demandes d'un client
export const useClientRequests = (clientId, initialFilters = {}) => {
  const { apiCall } = useApi();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });

  const loadRequests = useCallback(async (newFilters = {}) => {
    if (!clientId) return;
    
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger les demandes');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const combinedFilters = { ...filters, ...newFilters };
      const queryParams = new URLSearchParams();
      if (combinedFilters.status) queryParams.append('status', combinedFilters.status);
      if (combinedFilters.page) queryParams.append('page', combinedFilters.page);
      if (combinedFilters.limit) queryParams.append('limit', combinedFilters.limit);

      const endpoint = `/clients/${clientId}/requests${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const data = await apiCall(endpoint);
      
      setRequests(data.data || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0 });
    } catch (err) {
      console.error('Erreur lors du chargement des demandes:', err);
      setError(err.message);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, filters, apiCall]);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  return {
    requests,
    loading,
    error,
    filters,
    pagination,
    updateFilters,
    refetch: loadRequests
  };
};

// Hook pour gérer les paiements d'un client
export const useClientPaiements = (clientId, initialFilters = {}) => {
  const { apiCall } = useApi();
  const [paiements, setPaiements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });

  const loadPaiements = useCallback(async (newFilters = {}) => {
    if (!clientId) return;
    
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger les paiements');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const combinedFilters = { ...filters, ...newFilters };
      const queryParams = new URLSearchParams();
      if (combinedFilters.page) queryParams.append('page', combinedFilters.page);
      if (combinedFilters.limit) queryParams.append('limit', combinedFilters.limit);

      const endpoint = `/clients/${clientId}/payments${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const data = await apiCall(endpoint);
      
      setPaiements(data.data || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0 });
    } catch (err) {
      console.error('Erreur lors du chargement des paiements:', err);
      setError(err.message);
      setPaiements([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, filters, apiCall]);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  useEffect(() => {
    loadPaiements();
  }, [loadPaiements]);

  return {
    paiements,
    loading,
    error,
    filters,
    pagination,
    updateFilters,
    refetch: loadPaiements
  };
};

// Hook pour gérer les évaluations d'un client
export const useClientEvaluations = (clientId, initialFilters = {}) => {
  const { apiCall } = useApi();
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });

  const loadEvaluations = useCallback(async (newFilters = {}) => {
    if (!clientId) return;
    
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger les évaluations');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const combinedFilters = { ...filters, ...newFilters };
      const queryParams = new URLSearchParams();
      if (combinedFilters.page) queryParams.append('page', combinedFilters.page);
      if (combinedFilters.limit) queryParams.append('limit', combinedFilters.limit);

      const endpoint = `/clients/${clientId}/evaluations${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const data = await apiCall(endpoint);
      
      setEvaluations(data.data || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0 });
    } catch (err) {
      console.error('Erreur lors du chargement des évaluations:', err);
      setError(err.message);
      setEvaluations([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, filters, apiCall]);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  useEffect(() => {
    loadEvaluations();
  }, [loadEvaluations]);

  return {
    evaluations,
    loading,
    error,
    filters,
    pagination,
    updateFilters,
    refetch: loadEvaluations
  };
};